// components/portal/Grid.tsx
'use client';

import React, { memo, useCallback, useContext, useEffect, useMemo } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { InventoryEntityType, InventoryEntityState } from '#types/types';
import { ViewSection } from '@/components/portal/ViewSection';
import { useInventoryList, UseInventoryListProps } from './useInventoryList';
import { type InventoryFilterOption } from './InventoryFilterControls';
import { InventoryListToolbar } from './InventoryListToolbar';
import { InventoryMetricsBar } from './InventoryMetricsBar';
import { SkeletonCards } from '@/components/ui/Skeleton';
import clsx from 'clsx';
import { useKernel } from '@/kernel/Kernel';
import {
  getSessionTableFocusPageTitle,
  getSessionTableFocusViewForEntityType,
  isTableFocusSubject,
  resolvePortalViewFromTableFocus,
  selectActiveSubjectFromInventoryState,
} from '#lib/leasing/session-focus';
import { HostOwnsListNavigationContext } from './tableChromeContext';
import { useTableFocusRuntimePublication } from './useTableFocusRuntimePublication';

const ASSET_LEDGER_HEADER_STYLE: React.CSSProperties = {
  fontFamily: 'ui-sans-serif, -apple-system, system-ui, sans-serif',
};

interface GridProps<K extends InventoryEntityType> extends UseInventoryListProps<K> {
  renderCard: (item: InventoryEntityState<K>) => React.ReactNode;
  itemPredicate?: (item: InventoryEntityState<K>) => boolean;
  gridClass?: string;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  renderToolbarExtras?: (filters: Record<string, unknown>, setFilters: (f: Record<string, unknown>) => void, aggregations?: Record<string, unknown>) => React.ReactNode;
  renderCenterToolbarContent?: (filters: Record<string, unknown>, setFilters: (f: Record<string, unknown>) => void) => React.ReactNode;
  searchPlaceholder?: string;
  onCreateClick?: () => void;
  createButtonLabel?: string;
  beforeCreateContent?: React.ReactNode;
  /** The page's view switch, drawn in the title row (InventoryListToolbar.viewSwitch). */
  viewSwitch?: React.ReactNode;
  onVisibleItemIdsChange?: (ids: string[]) => void;
  embedded?: boolean;
  /** The page heading, drawn by the toolbar (ChatGPT's page header). */
  pageTitle?: string;
}

function GridInner<K extends InventoryEntityType>({
  entityType,
  initialFilters,
  lockedFilters,
  scopedIds,
  sortOptions,
  filterOptions,
  pageSize = 16,
  disableManualRefresh,
  onManualRefresh,
  renderCard,
  itemPredicate,
  gridClass = "grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
  assetLedgerTitle,
  assetLedgerBreadcrumb,
  renderToolbarExtras,
  renderCenterToolbarContent,
  searchPlaceholder = "Search...",
  onCreateClick,
  createButtonLabel,
  beforeCreateContent,
  viewSwitch,
  onVisibleItemIdsChange,
  embedded,
  pageTitle,
}: GridProps<K>) {
  const { kernel } = useKernel();
  const activeSubject = useKernelStore(selectActiveSubjectFromInventoryState);
  const {
    viewItems,
    viewIsLoading,
    refreshIsLoading,
    viewPagination,
    viewFilters,
    setFilters,
    hasLockedFilters,
    isFilterKeyLocked,
    mergeWithLockedFilters,
    handleSearchChange,
    handleSortChange,
    handleManualRefresh,
    handleReachBottom,
  } = useInventoryList({
    entityType,
    pageSize,
    initialFilters,
    lockedFilters,
    scopedIds,
    sortOptions,
    filterOptions,
    disableManualRefresh,
    onManualRefresh
  });
  const typedViewFilters = viewFilters as Record<string, unknown>;
  const isEmbedded = embedded ?? hasLockedFilters;
  const toolbarFilterOptions = (filterOptions ?? []) as InventoryFilterOption[];

  const tableFocusView = useMemo(
    () => getSessionTableFocusViewForEntityType(entityType as any),
    [entityType]
  );
  const isCurrentSessionTableFocus = useMemo(() => {
    if (!tableFocusView) return false;
    if (!isTableFocusSubject(activeSubject)) return false;
    const activeTableView = resolvePortalViewFromTableFocus(activeSubject);
    return activeTableView === tableFocusView;
  }, [activeSubject, tableFocusView]);
  const hostOwnsNavigation = useContext(HostOwnsListNavigationContext);
  const lang = useKernelStore((state) => state.language);
  const headerTitle = pageTitle ?? (!hasLockedFilters && !hostOwnsNavigation
    ? (assetLedgerTitle || (isCurrentSessionTableFocus && tableFocusView ? getSessionTableFocusPageTitle(tableFocusView, lang === 'fr' ? 'fr' : 'en') : undefined))
    : undefined);
  const currentSortLabel = useMemo(
    () => sortOptions.find((option) => option.field === String(typedViewFilters.sortBy || ''))?.label || 'Sort',
    [sortOptions, typedViewFilters.sortBy]
  );
  const displayItems = useMemo(
    () => (itemPredicate ? viewItems.filter((item) => itemPredicate(item)) : viewItems),
    [itemPredicate, viewItems]
  );
  const visibleItemIds = useMemo(() => displayItems.map((item) => item.id), [displayItems]);
  const visibleItemIdsSignature = useMemo(() => visibleItemIds.join(','), [visibleItemIds]);

  useTableFocusRuntimePublication({
    entityType,
    presentationMode: 'grid',
    viewFilters,
    pageSize,
    visibleItemIds,
    enabled: !isEmbedded,
  });

  useEffect(() => {
    onVisibleItemIdsChange?.(visibleItemIds);
  }, [onVisibleItemIdsChange, visibleItemIds, visibleItemIdsSignature]);

  return (
    <ViewSection 
      wrapperClassName={isEmbedded ? 'h-auto min-h-0' : undefined}
      contentClassName={'!px-0 !py-0 !space-y-0'}
      onReachBottom={handleReachBottom}
      hideViewLabel={true}
      useAbsoluteScrollContainer={!isEmbedded}
    >
      <InventoryListToolbar
        beforeCreateContent={beforeCreateContent}
        onCreateClick={onCreateClick}
        createButtonLabel={createButtonLabel}
        searchValue={String(typedViewFilters.search || '')}
        onSearchChange={handleSearchChange}
        searchPlaceholder={searchPlaceholder}
        disableManualRefresh={disableManualRefresh}
        refreshIsLoading={refreshIsLoading}
        onManualRefresh={handleManualRefresh}
        beforeFilterContent={renderCenterToolbarContent?.(viewFilters, setFilters)}
        filterOptions={toolbarFilterOptions}
        viewFilters={typedViewFilters}
        isFilterKeyLocked={isFilterKeyLocked}
        mergeWithLockedFilters={mergeWithLockedFilters}
        setFilters={setFilters}
        sortOptions={sortOptions}
        activeSortField={String(typedViewFilters.sortBy || '')}
        activeSortDir={String(typedViewFilters.sortDir || 'desc')}
        onSortChange={handleSortChange}
        currentSortLabel={currentSortLabel}
        rightToolbarContent={renderToolbarExtras?.(viewFilters, setFilters, viewPagination?.aggregations ?? undefined)}
        viewSwitch={viewSwitch}
        title={headerTitle}
      />

      {/* Summary metrics on top of the grid (main list views only). */}
      {!hasLockedFilters && (
        <InventoryMetricsBar entityType={entityType} items={viewItems} total={viewPagination?.total ?? null} aggregations={viewPagination?.aggregations ?? null} className="custom-scrollbar mx-6 mt-1 overflow-x-auto pb-5" />
      )}

      <div className={clsx("flex-1 px-6", headerTitle ? 'pb-6' : 'py-6', hasLockedFilters ? 'min-h-0' : 'min-h-[200px]')}>
        <div className={clsx("grid gap-4 gap-y-7", gridClass)}>
          {displayItems.map(item => renderCard(item))}
        </div>

        {viewIsLoading && displayItems.length === 0 && <SkeletonCards className={clsx('grid gap-4 gap-y-7', gridClass)} />}

        {!viewIsLoading && displayItems.length === 0 && (
          <div className="p-12 text-center text-white/20 text-sm italic">
            No results found.
          </div>
        )}
      </div>
    </ViewSection>
  );
}

export const Grid = memo(GridInner) as typeof GridInner;
